/**
 * Extract a human-readable message string from an Axios error.
 *
 * Backend error bodies come in several shapes:
 *   - a 500: `{ detail: "Internal server error", status_code: 500 }`
 *   - DRF validation/permission: `{ detail: "..." }`
 *   - order create (custom handler): `{ detail: { errors: ["..."] }, status_code: 400 }`
 *   - order split/convert: `{ error: ["..."] }`
 *   - purchase order create: a bare string
 *   - typed accounting-sync errors: an HTML string
 *
 * Rendering a non-string body directly in an alert produces "[object Object]".
 * This walks the body across the known shapes and always returns a string.
 *
 * @param {*} errors - the caught Axios error
 * @param {string} fallback - message to use when nothing readable is found
 * @returns {string}
 */
const extractMessage = (value) => {
  if (value == null) {
    return "";
  }
  if (typeof value === "string") {
    return value.trim();
  }
  if (Array.isArray(value)) {
    return value.map(extractMessage).filter(Boolean).join(" ");
  }
  if (typeof value === "object") {
    // Known error keys, in priority order. `detail`/`errors` may themselves be
    // strings, arrays, or nested objects, so recurse.
    return (
      extractMessage(value.detail) ||
      extractMessage(value.errors) ||
      extractMessage(value.error) ||
      extractMessage(value.message) ||
      ""
    );
  }
  return "";
};

export const getApiErrorMessage = (
  errors,
  fallback = "Something went wrong, please try again."
) => {
  return extractMessage(errors?.response?.data) || fallback;
};
